"use client";

import { CSSProperties, ComponentRef, forwardRef, useCallback, useEffect, useId, useRef, useState } from "react";
import { DoubleArrow } from "@mui/icons-material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { Box, Button } from "@mui/material";
import { BoxProps } from "@mui/system";
import clsx from "clsx";
import styles from "./VisiblyScrollable.module.css";
import useIsDesktop from "@/hooks/useDesktop";

type FadeSize = "sm" | "md";

interface VisiblyScrollableProps extends BoxProps {
    fadeSize?: FadeSize;
    overscroll?: boolean;
    scrollDisabled?: boolean;
    scrollContainerAriaLabel?: string;
}

function getFadeFactor(fadeSize: FadeSize): number {
    switch (fadeSize) {
        case "sm":
            return 2;
        case "md":
            return 4;
    }
}

const VisiblyScrollableV2 = forwardRef<
    ComponentRef<typeof Box>,
    VisiblyScrollableProps
>(
    ({
        children,
        overscroll = true,
        fadeSize = "md",
        scrollDisabled = false,
        scrollContainerAriaLabel,
        className,
        style,
        ...props
    }, ref) => {
        const isDesktop = useIsDesktop();

        const fadeFactor = getFadeFactor(fadeSize);
        const rootRef = useRef<HTMLDivElement | null>(null);
        const scrollableRef = useRef<HTMLDivElement | null>(null);
        const rafRef = useRef<number | null>(null);
        const [canScrollUp, setCanScrollUp] = useState(false);
        const [canScrollDown, setCanScrollDown] = useState(false);
        const [isMobileExpanded, setIsMobileExpanded] = useState(false);
        const [hasMobileOverflow, setHasMobileOverflow] = useState(false);
        const [mobileCollapsedHeight, setMobileCollapsedHeight] = useState<number>(0);
        const [mobileExpandedHeight, setMobileExpandedHeight] = useState<number>(0);
        const mobileContentId = useId();

        const measure = useCallback(() => {
            const el = scrollableRef.current;
            if (!el) {
                return;
            }

            if (scrollDisabled) {
                setCanScrollUp(false);
                setCanScrollDown(false);
                setHasMobileOverflow(false);
                return;
            }

            if (!isDesktop) {
                setMobileCollapsedHeight(el.clientHeight);
                setMobileExpandedHeight(el.scrollHeight);
                if (!isMobileExpanded) {
                    const overflow = el.scrollHeight > el.clientHeight + 1;
                    setHasMobileOverflow(overflow);
                }
                setCanScrollUp(false);
                setCanScrollDown(false);
                return;
            }

            const scrollTop = Math.max(0, el.scrollTop);
            const clientHeight = el.clientHeight;
            const scrollHeight = el.scrollHeight;
            const epsilon = 0.5;

            const up = scrollTop > epsilon;
            const down = scrollTop + clientHeight < scrollHeight - epsilon;

            setCanScrollUp(up);
            setCanScrollDown(down);
        }, [isDesktop, isMobileExpanded, scrollDisabled]);

        const scheduleMeasure = useCallback(() => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }

            rafRef.current = requestAnimationFrame(() => {
                measure();
            });
        }, [measure]);

        useEffect(() => {
            const scrollable = scrollableRef.current;

            if (!scrollable) {
                return;
            }

            const onScroll = () => {
                scheduleMeasure();
            };

            if (isDesktop && !scrollDisabled) {
                scrollable.addEventListener("scroll", onScroll, { passive: true });
            }
            window.addEventListener("resize", scheduleMeasure, { passive: true });

            const resizeObserver = new ResizeObserver(() => {
                scheduleMeasure();
            });
            resizeObserver.observe(scrollable);
            scheduleMeasure();

            return () => {
                if (isDesktop && !scrollDisabled) {
                    scrollable.removeEventListener("scroll", onScroll);
                }
                window.removeEventListener("resize", scheduleMeasure);
                resizeObserver.disconnect();

                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                }
            };
        }, [isDesktop, scheduleMeasure, scrollDisabled]);

        const rootStyle = {
            ...style,
            ["--vs-fade-size" as string]: `calc(var(--current-line-height, 1.5) * var(--current-font-size, 16px) * ${fadeFactor})`,
            ["--vs-indicator-height" as string]: "calc(var(--current-line-height, 1.5) * var(--current-font-size, 16px) * 2)",
            ["--vs-overscroll-behavior" as string]: overscroll ? "auto" : "contain",
        } satisfies CSSProperties;

        const isExpandedOnMobile = !isDesktop && !scrollDisabled && isMobileExpanded;
        const showMobileReadMore = !isDesktop && !scrollDisabled && hasMobileOverflow;
        const showCollapsedReadMore = showMobileReadMore && !isExpandedOnMobile;
        const isKeyboardScrollable = isDesktop && !scrollDisabled && (canScrollUp || canScrollDown);

        const setRootRef = useCallback((node: HTMLDivElement | null) => {
            rootRef.current = node;
            if (!ref) return;
            if (typeof ref === "function") {
                ref(node);
                return;
            }
            ref.current = node;
        }, [ref]);

        const mobileInnerStyle = !isDesktop && showMobileReadMore
            ? ({
                ["--vs-mobile-collapsed-height" as string]: `${mobileCollapsedHeight}px`,
                ["--vs-mobile-expanded-height" as string]: `${mobileExpandedHeight}px`,
            } satisfies CSSProperties)
            : undefined;

        const handleMobileCollapse = useCallback(() => {
            setIsMobileExpanded(false);

            const scrollTarget = rootRef.current;
            if (!scrollTarget || typeof window === "undefined") return;

            const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            window.requestAnimationFrame(() => {
                scrollTarget.scrollIntoView({
                    behavior: prefersReducedMotion ? "auto" : "smooth",
                    block: "start",
                    inline: "nearest",
                });
            });
        }, []);

        return (
            <Box
                ref={setRootRef}
                {...props}
                style={rootStyle}
                data-can-scroll-up={canScrollUp}
                data-can-scroll-down={canScrollDown}
                data-mobile={isDesktop ? "false" : "true"}
                data-mobile-expanded={isExpandedOnMobile ? "true" : "false"}
                className={clsx(
                    "visibly-scrollable-root relative flex flex-1 min-h-0 min-w-0 max-h-full overflow-hidden",
                    isDesktop ? "flex-row" : "flex-col",
                    styles.root,
                    className
                )}
            >
                {isDesktop && !scrollDisabled && (
                    <>
                        <Box aria-hidden className={clsx(styles.indicator, styles.indicatorTop)}>
                            <DoubleArrow
                                className={styles.arrow}
                                sx={{
                                    color: "primary.main",
                                    opacity: 0.9,
                                }}
                            />
                        </Box>

                        <Box aria-hidden className={clsx(styles.indicator, styles.indicatorBottom)}>
                            <DoubleArrow
                                className={styles.arrow}
                                sx={{
                                    color: "primary.main",
                                    opacity: 0.9,
                                }}
                            />
                        </Box>
                    </>
                )}

                <Box
                    ref={scrollableRef}
                    role={isKeyboardScrollable ? "region" : undefined}
                    aria-label={isKeyboardScrollable ? scrollContainerAriaLabel : undefined}
                    tabIndex={isKeyboardScrollable ? 0 : undefined}
                    className={clsx(
                        "visibly-scrollable-inner relative flex flex-col flex-1 min-h-0",
                        isDesktop
                            ? (scrollDisabled ? "overflow-hidden" : "overflow-auto no-scrollbar")
                            : "overflow-hidden",
                        showCollapsedReadMore && styles.mobileContentInset,
                        showMobileReadMore && styles.mobileExpandable,
                        isExpandedOnMobile && styles.mobileExpanded,
                        styles.inner
                    )}
                    id={mobileContentId}
                    style={mobileInnerStyle}
                >
                    {children}
                </Box>

                {showCollapsedReadMore && (
                    <Box className={styles.mobileReadMoreOverlay}>
                        <Button
                            variant="outlined"
                            color="primary"
                            endIcon={<ExpandMore />}
                            aria-expanded={isExpandedOnMobile ? "true" : "false"}
                            aria-controls={mobileContentId}
                            className={styles.readMoreButton}
                            onClick={() => setIsMobileExpanded(true)}
                        >
                            Mehr lesen
                        </Button>
                    </Box>
                )}

                {isExpandedOnMobile && showMobileReadMore && (
                    <Box className={styles.mobileReadMoreFooter}>
                        <Button
                            className={styles.readLessButton}
                            variant="outlined"
                            color="primary"
                            endIcon={<ExpandLess />}
                            aria-expanded="true"
                            aria-controls={mobileContentId}
                            onClick={handleMobileCollapse}
                        >
                            Weniger anzeigen
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }
);

VisiblyScrollableV2.displayName = "VisiblyScrollableV2";
export default VisiblyScrollableV2;
